import HBIcon from "@/components/HBIcon";
import HBConfirm from "@/components/HBModal/confirm";
import HBPopover from "@/components/HBPopover";
import { COMPANY_UPGRADE_PLUS } from "@/constants/vip";
import useAppStore from "@/context/store";
import { getUpdatePrice } from "@/services";
import useRequest from "@/utils/useReq";
import classNames from "classnames";
import dayjs from "dayjs";
import { isNumber, round } from "lodash";
import Link from "next/link";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PayBottom from "../pay/payBottom";
import { VipDialogContext } from "../shared/configContext";
import { getPayDetailByGood, getRightPkgCode } from "../shared/utils";
import VipCheckbox from "../vipCheckbox/checkBox";
import VipRadio from "../vipCheckbox/radio";
import styles from "./index.module.less";

function getGoodSkuByCode(goods, code) {
  return goods.find((g) => g.code === code)?.skus[0] || {};
}

export default function UpgradeTab({
  goods,
  defaultSelectedPkg,
  onOrderDone,
  isExpVip,
  isUpgrade,
  onDisableChange,
  onTransfer,
}) {
  const { user, setUser } = useAppStore(({ user, setUser }) => ({
    user,
    setUser,
  }));
  const { config, rightData } = useContext(VipDialogContext);
  const { left, upgrade: companyTabConf } = config;
  const { goodCode, authMod, downloadMod } = companyTabConf;

  const [disabled, setDisabled] = useState(false);
  const onDisableChangeWrap = useCallback(
    (state) => {
      setDisabled(state);
      onDisableChange && onDisableChange(state);
    },
    [onDisableChange]
  );

  //续费场景下默认选中的套餐
  const defaultUpgrage = useMemo(() => {
    if (defaultSelectedPkg) {
      const matchedPkg = downloadMod.modList.find((good) =>
        defaultSelectedPkg.includes(good.goodCode)
      )?.goodCode;

      if (matchedPkg) return matchedPkg;
    }
    if (isUpgrade) return;

    if (rightData.upgrade_rights.length) {
      return downloadMod.modList[0].goodCode;
    }
    if (rightData.upgrade_plus_rights.length) {
      return downloadMod.modList[1].goodCode;
    }
  }, [defaultSelectedPkg, downloadMod, rightData, isUpgrade]);

  const defaultAuthPkg = useMemo(() => {
    if (defaultSelectedPkg) {
      return authMod.modList.find((good) =>
        defaultSelectedPkg.includes(good.goodCode)
      )?.goodCode;
    }
    if (isUpgrade) return;
    if (rightData.auth_rights.length) {
      return authMod.modList[0].goodCode;
    }
  }, [defaultSelectedPkg, authMod, rightData, isUpgrade]);

  useEffect(() => {
    const { tem_rights } = rightData;

    if (isUpgrade && tem_rights.length) {
      HBConfirm.open({
        title: "提示",
        content:
          "若现在升级，「个人VIP24小时素材包」将不能使用，建议你使用后再升级企业VIP。",
        hideCancelBtn: true,
        okText: "确认",
      });
    }
  }, [isUpgrade, rightData]);

  const vipGoodSku = useMemo(() => {
    return getGoodSkuByCode(goods, goodCode);
  }, [goodCode, goods]);

  // 选中的升级包
  const [upgradePkg, setUpgradePkg] = useState(defaultUpgrage);
  // 扩展授权包
  const [authPkg, setAuthPkg] = useState(defaultAuthPkg);

  // 升级场景下的折抵计算
  const { data: discount, loading: discountLoading } = useRequest(async () => {
    if (isExpVip || isUpgrade) {
      const calc = await getUpdatePrice({
        scene: "upgrade",
        promotion: {
          activities: [],
          user_coupon_ids: [],
        },
        skus: [
          {
            quantity: 1,
            sku_id: vipGoodSku.id,
            is_upgrade: true,
          },
        ],
      });

      const discount = round(calc.total_price - calc.actual_total_price, 2);

      return {
        type: "discount",
        name: isExpVip ? "体验会员抵扣" : "个人VIP升级抵扣",
        price: -discount,
        number: 1,
        expires: dayjs(calc.upgrade_vip_end_at, "YYYY-MM-DD HH:mm:ss"),
        originPrice: calc.total_price,
      };
    }

    return null;
  });

  const upgradeYear = useMemo(() => {
    if (discount) {
      return round(discount.expires.diff(dayjs(new Date()), "y", true), 2);
    }
  }, [discount]);

  const payDetail = useMemo(() => {
    if (discountLoading || !rightData) return null;

    const details = [
      getPayDetailByGood(
        goods.find((g) => g.code === goodCode),
        1,
        {
          start: isUpgrade ? discount?.expires : rightData.vip_expire,
          end: isUpgrade ? discount?.expires : 0,
          isUpgrade,
          extend: discount
            ? { price: discount.originPrice, isUpgrade }
            : { isUpgrade },
        }
      ),
    ];

    if (upgradePkg) {
      let upgradePkgExpire;
      const upgradeGood = goods.find((g) => g.code === upgradePkg);
      if (COMPANY_UPGRADE_PLUS === getRightPkgCode(upgradeGood)) {
        upgradePkgExpire = rightData.upgrade_plus_rights_expire;
      } else {
        upgradePkgExpire = rightData.upgrade_rights_expire;
      }

      details.push(
        getPayDetailByGood(upgradeGood, 1, {
          start: isUpgrade ? 0 : upgradePkgExpire,
          end: isUpgrade ? discount?.expires : 0,
          isUpgrade,
        })
      );
    }

    if (authPkg) {
      details.push(
        getPayDetailByGood(
          goods.find((g) => g.code === authPkg),
          1,
          {
            start: isUpgrade ? 0 : rightData.auth_rights_expire,
            end: isUpgrade ? discount?.expires : 0,
            isUpgrade,
          }
        )
      );
    }

    return details;
  }, [
    goods,
    rightData,
    upgradePkg,
    authPkg,
    goodCode,
    isUpgrade,
    discount,
    discountLoading,
  ]);

  const onOrderDoneWrap = useCallback(
    (order) => {
      onOrderDone && onOrderDone(order);
      // 更新用户正确信息
      setUser({
        ...user,
        extra: {
          ...(user.extra || {}),
          material_vip: 20,
          material_vip_expire_at: payDetail[0].expire,
        },
      });
    },
    [onOrderDone, setUser, user, payDetail]
  );

  const detailData = useMemo(() => {
    return discount ? [...payDetail, discount] : payDetail;
  }, [discount, payDetail]);

  return (
    <>
      <div className={styles.leftInfo}>
        {isUpgrade ? (
          <UpgradeLeft discount={discount} />
        ) : (
          <>
            <div>{left.title}</div>
            <ul>
              {left.list.map((item) => (
                <li key={item.title}>
                  <h6>
                    {item.title}
                    <div
                      className={styles.line}
                      style={{ background: left.line }}
                    ></div>
                  </h6>
                  <p>{item.desc}</p>
                </li>
              ))}
            </ul>
          </>
        )}
        {/* <UpgradeLeft /> */}

        <div className={styles.links}>
          <Link href="/vip/info">
            <a target="_blank">了解更多</a>
          </Link>
          ｜
          <Link href="https://support.qq.com/products/30852/faqs-more/#classifyId=87404">
            <a target="_blank">常见问题</a>
          </Link>
          ｜
          <Link href="/vip/exchange">
            <a target="_blank">会员兑换</a>
          </Link>
        </div>
      </div>
      <div
        className={styles.right}
        data-order-type="会员升级"
        data-good-second-type="企业VIP"
      >
        <div className={styles.upgradeTab}>
          <div className={styles.vip}>
            <div className={styles.title}>{companyTabConf.title}</div>
            <div className={styles.desc}>
              <span>
                {companyTabConf.authText}
                <HBPopover
                  content={
                    <>
                      <div
                        className={styles.tipText}
                        dangerouslySetInnerHTML={{
                          __html: companyTabConf.authTip,
                        }}
                      ></div>
                      <Link href="/vip/info?anchor=discoveryRange">
                        <a className="color-primary" target="_blank">
                          查看详情授权范围
                        </a>
                      </Link>
                    </>
                  }
                  overlayInnerStyle={{ width: 300 }}
                  placement="bottom"
                >
                  <HBIcon type="ic_help" />
                </HBPopover>
              </span>
              <span>{companyTabConf.download}</span>
            </div>
            <div className={styles.price}>
              <div className={styles.actualPrice}>
                ¥
                <span>
                  {discount ? discount.originPrice : vipGoodSku.price}
                </span>
                {upgradeYear && upgradeYear > 1 ? `(${upgradeYear}年)` : "/年"}
              </div>
              {vipGoodSku.original_price > vipGoodSku.price && (
                <div className={styles.origin}>
                  ¥{vipGoodSku.original_price}
                  /年
                </div>
              )}
            </div>
          </div>

          <div className={styles.mod}>
            <div className={styles.modName}>{downloadMod.modName}</div>
            <div className={styles.packageList}>
              {downloadMod.modList.map((pkg, index) => (
                <div
                  className={classNames(styles.package, {
                    [styles.disabled]: disabled,
                    [styles.active]: upgradePkg === pkg.goodCode,
                  })}
                  key={index}
                  onClick={() =>
                    !disabled &&
                    setUpgradePkg(
                      upgradePkg !== pkg.goodCode ? pkg.goodCode : null
                    )
                  }
                >
                  <VipRadio
                    checked={upgradePkg === pkg.goodCode}
                    onChange={(checked) =>
                      !disabled && setUpgradePkg(checked ? pkg.goodCode : null)
                    }
                  />
                  <div className={styles.info}>
                    <div dangerouslySetInnerHTML={{ __html: pkg.title }}></div>
                    <div>{pkg.desc}</div>
                  </div>
                  <div className={styles.price}>
                    + ¥
                    <span>{getGoodSkuByCode(goods, pkg.goodCode).price}</span>/
                    {pkg.unit}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.mod}>
            <div className={styles.modName}>{authMod.modName}</div>
            <div className={styles.packageList}>
              {authMod.modList.map((pkg, index) => (
                <div
                  className={classNames(styles.package, {
                    [styles.disabled]: disabled,
                    [styles.active]: authPkg === pkg.goodCode,
                  })}
                  key={index}
                  onClick={() =>
                    !disabled &&
                    setAuthPkg(authPkg !== pkg.goodCode ? pkg.goodCode : null)
                  }
                >
                  <VipCheckbox
                    checked={authPkg === pkg.goodCode}
                    onChange={(checked) =>
                      !disabled && setAuthPkg(checked ? pkg.goodCode : null)
                    }
                  />
                  <div className={styles.info}>
                    <div dangerouslySetInnerHTML={{ __html: pkg.title }}></div>
                    <div>{pkg.desc}</div>
                  </div>
                  <div className={styles.price}>
                    + ¥
                    <span>{getGoodSkuByCode(goods, pkg.goodCode).price}</span>/
                    {pkg.unit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <PayBottom
          hasSubscribeTip
          scene={isUpgrade ? "upgrade" : "normal"}
          details={detailData}
          onOrderDone={onOrderDoneWrap}
          onDisableChange={onDisableChangeWrap}
          onTransfer={onTransfer}
        />
      </div>
    </>
  );
}

function UpgradeLeft({ discount }) {
  return (
    <div className={styles.upgradeLeft}>
      <div className={styles.title}>升级企业VIP</div>
      <div>
        升级后企业VIP立即生效，{dayjs(discount?.expires).format("YYYY/MM/DD")}
        到期
      </div>
      <div className={styles.flow}>
        <div className={styles.title}>如何升级企业VIP</div>
        <div>
          <div className={classNames(styles.flowItem, styles.done)}>
            <div>评估个人VIP抵扣价值</div>
            <div className={styles.primary}>
              当前可抵扣¥{isNumber(discount?.price) ? -discount?.price : "--"}
            </div>
          </div>
          <div className={classNames(styles.flowItem, styles.current)}>
            选择您的企业VIP方案
          </div>
          <div className={styles.flowItem}>扫码支付抵扣后的金额</div>
        </div>
      </div>
    </div>
  );
}
